/**
 * @file 用户管理页（Users）
 *
 * 该文件定义了 CRM 系统的用户管理组件 `CrmUsersPage`。
 * 主要功能包括：
 * - 用户列表展示（表格形式，含角色徽章和状态标签）
 * - 按角色和状态筛选用户
 * - 新增/编辑用户（模态框表单，支持用户名、密码、邮箱、角色、状态、QQ、Skype）
 * - 删除用户（带确认对话框）
 * - 分页导航
 *
 * 数据来源：`userApi`（CRUD 操作）
 *
 * 设计决策：
 * - 使用 `<crm-select>` 组件实现角色和状态筛选下拉框
 * - 使用 `<crm-pagination>` 组件实现分页
 * - 使用 `<crm-table-toolbar>` 组件实现刷新和计数
 * - 使用 `<crm-loading-overlay>` 组件实现加载遮罩
 * - 支持 ESC 键关闭模态框
 * - 模态框背景点击关闭（使用 mousedown + click 模式避免误触）
 */

import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { userApi } from '../api.js';
import { BaseElement } from '../base-element.js';
import type { User, CreateUserRequest, UpdateUserRequest } from '../types.js';
import { toast } from '../components/toast.js';
import '../components/table-toolbar.js';
import '../components/loading-overlay.js';
import '../components/pagination.js';
import { showConfirm } from '../components/confirm-dialog.js';
import { isNetworkError } from '../utils/index.js';

/**
 * 用户管理页组件
 *
 * 继承 `BaseElement`，注册为 `<crm-users-page>` 自定义元素。
 * 提供用户的增删改查（CRUD）功能，支持角色和状态筛选、分页。
 *
 * 状态字段：
 * - `users` — 当前页用户列表
 * - `isLoading` — 加载状态，控制遮罩和按钮禁用
 * - `total` / `currentPage` / `perPage` — 分页参数
 * - `filterRole` / `filterStatus` — 筛选条件
 * - `showModal` / `editingUser` — 模态框开关和编辑目标
 * - `formRole` / `formStatus` — 表单中的角色和状态（通过 crm-select 控制）
 */
@customElement('crm-users-page')
export class CrmUsersPage extends BaseElement {
  /** 当前页用户列表 */
  @state()
  private users: User[] = [];

  /** 加载状态标志，控制 loading overlay 显示 */
  @state()
  private isLoading = false;
  /** 列表请求序号，仅允许最后一次筛选请求更新页面。 */
  private _listRequestId = 0;

  /** 用户总数，用于分页组件计算总页数 */
  @state()
  private total = 0;

  /** 当前页码，从 1 开始 */
  @state()
  private currentPage = 1;

  /** 每页条数，默认 20 */
  @state()
  private perPage = 20;

  /** 角色筛选值，空字符串表示所有角色 */
  @state()
  private filterRole = '';

  /** 状态筛选值：1(启用) / 0(禁用) / null(全部) */
  @state()
  private filterStatus: number | null = null;

  /** 是否显示新增/编辑模态框 */
  @state()
  private showModal = false;

  /** 当前编辑的用户对象，为 null 时表示新增模式 */
  @state()
  private editingUser: User | null = null;

  /** 表单中的角色值，通过 crm-select 绑定 */
  @state()
  private formRole = 'user';

  /** 表单中的状态值（字符串形式），通过 crm-select 绑定 */
  @state()
  private formStatus = '1';

  /** 组件样式（空，依赖全局 Tailwind 样式） */
  static styles = css``;

  /**
   * 根据角色返回对应的徽章样式类名
   * @param role - 用户角色：admin(红色) / user(蓝色) / readonly(绿色) / 默认(灰色)
   * @returns Tailwind 背景和文字颜色类名
   */
  private getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'admin': return 'bg-error/10 text-error';
      case 'user': return 'bg-primary/10 text-primary';
      case 'readonly': return 'bg-success/10 text-success';
      default: return 'bg-bg text-text-muted';
    }
  }

  /**
   * 将角色英文标识转换为中文标签
   * @param role - 用户角色英文标识
   * @returns 中文标签字符串，未匹配时返回原始值
   */
  private getRoleLabel(role: string): string {
    const labels: Record<string, string> = { admin: '管理员', user: '普通用户', readonly: '只读用户' };
    return labels[role] || role;
  }

  /**
   * 组件挂载到 DOM 后的生命周期回调
   *
   * 调用父类方法后：
   * 1. 加载用户列表
   * 2. 注册全局 ESC 键监听器用于关闭模态框
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadUsers();
    // 注册 ESC 键监听，使模态框可通过按 Escape 键关闭
    document.addEventListener('keydown', this.handleEscKey);
  }

  /**
   * 组件从 DOM 移除时的生命周期回调
   *
   * 调用父类方法后移除全局 ESC 键监听器，防止内存泄漏。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleEscKey);
  }

  /**
   * ESC 键事件处理器（箭头函数，保持 this 绑定）
   *
   * 当模态框打开时按 ESC 键关闭模态框。
   * @param e - 键盘事件对象
   */
  private handleEscKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.showModal) {
      this.closeModal();
    }
  };

  /**
   * 加载用户列表
   *
   * 根据当前分页和筛选条件调用 `userApi.list()` 获取用户数据。
   * 成功后更新用户列表、总数、当前页码和每页条数。
   * 失败时仅输出日志，不弹 toast（避免刷新时频繁提示）。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async loadUsers() {
    const requestId = ++this._listRequestId;
    this.isLoading = true;
    try {
      // 构建查询参数，仅添加非空筛选条件
      const params: Record<string, any> = { page: this.currentPage, per_page: this.perPage };
      if (this.filterRole) params.role = this.filterRole;
      if (this.filterStatus !== null) params.status = this.filterStatus;
      const response = await userApi.list(params);
      if (requestId !== this._listRequestId) return;
      this.users = response.data;
      this.total = response.pagination.total;
      this.currentPage = response.pagination.current_page;
      this.perPage = response.pagination.per_page;
    } catch (err: any) {
      if (requestId === this._listRequestId) console.error('Failed to load users:', err);
    } finally {
      if (requestId === this._listRequestId) this.isLoading = false;
    }
  }

  /**
   * 分页变更处理器
   *
   * 更新当前页码后重新加载用户列表。
   * @param page - 目标页码
   */
  private handlePageChange(page: number) {
    this.currentPage = page;
    this.loadUsers();
  }

  /**
   * 打开新增/编辑模态框
   *
   * 不传参数时为新增模式，传入用户对象时为编辑模式。
   * 根据模式初始化表单字段（角色、状态）。
   * @param user - 可选的用户对象，传入时为编辑模式
   */
  private openModal(user?: User) {
    this.editingUser = user || null;
    // 编辑模式预填表单字段，新增模式使用默认值
    this.formRole = user?.role || 'user';
    this.formStatus = String(user?.status ?? 1);
    this.showModal = true;
  }

  /**
   * 关闭模态框并重置表单状态（箭头函数，保持 this 绑定）
   */
  private closeModal = () => {
    this.showModal = false;
    this.editingUser = null;
    this.formRole = 'user';
    this.formStatus = '1';
  }

  /**
   * 表单提交处理器
   *
   * 从 FormData 收集表单数据，根据编辑/新增模式分别调用 update/create API。
   * - 编辑模式：密码留空则不修改，有值时才更新密码
   * - 新增模式：密码必填，为空时弹错误提示并中止
   * 成功后关闭模态框并重新加载列表。
   *
   * @param e - 表单提交事件
   * @returns 无返回值（Promise<void>）
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    // 构建请求体，角色和状态从 @state 字段获取（由 crm-select 控制）
    const data: Record<string, any> = {
      username: formData.get('username') as string,
      email_address: formData.get('email_address') as string,
      role: this.formRole,
      status: parseInt(this.formStatus),
      qq: formData.get('qq') as string,
      skype: formData.get('skype') as string,
    };

    const password = formData.get('password') as string;
    if (this.editingUser) {
      // 编辑模式：密码有值时才更新，留空表示不修改
      if (password) data.password = password;
      await userApi.update(this.editingUser.id, data as UpdateUserRequest);
    } else {
      // 新增模式：密码必填
      if (!password) { toast.error('密码不能为空'); return; }
      data.password = password;
      await userApi.create(data as CreateUserRequest);
    }

    this.closeModal();
    this.loadUsers();
  }

  /**
   * 删除用户
   *
   * 弹出确认对话框，用户确认后调用 `userApi.delete()` 删除用户。
   * 成功后重新加载列表。失败时显示错误 toast（过滤网络错误）。
   *
   * @param user - 待删除的用户对象
   * @returns 无返回值（Promise<void>）
   */
  private async handleDelete(user: User) {
    if (!(await showConfirm(`确定要删除用户 "${user.username}" 吗？`))) return;
    try {
      await userApi.delete(user.id);
      this.loadUsers();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '删除失败');
    }
  }

  /**
   * 渲染通用模态框容器
   *
   * 提供模态框的背景遮罩、标题栏、关闭按钮和内容容器。
   * 支持点击背景区域关闭（使用 mousedown + click 模式避免拖拽选中文本时误触发关闭）。
   *
   * @param title - 模态框标题文本
   * @param body - 模态框内容（Lit TemplateResult）
   * @param onClose - 关闭回调函数
   * @returns Lit TemplateResult
   */
  private renderModal(title: string, body: any, onClose: () => void) {
    // 记录 mousedown 是否发生在背景遮罩上，用于 click 时判断是否应关闭
    let backdropMousedown = false;
    return html`
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (backdropMousedown) onClose(); }}>
        <div class="bg-surface rounded-md shadow-xl w-full max-w-xl sm:max-w-2xl max-h-[90vh] overflow-y-auto border border-border mx-4" @click=${(e: Event) => e.stopPropagation()}>
          <div class="sticky top-0 bg-surface flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
            <h2 class="m-0 font-semibold text-xl text-text">${title}</h2>
            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="p-6">${body}</div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染用户管理页面
   *
   * 页面布局：
   * 1. 顶部工具栏：标题、角色筛选、状态筛选、新增按钮、刷新/计数工具栏
   * 2. 表格区域：用户列表（含加载遮罩、空状态、角色徽章、状态标签、操作按钮）
   * 3. 分页组件
   * 4. 模态框（条件渲染）：新增/编辑用户表单
   *
   * @returns Lit TemplateResult
   */
  render() {
    return html`
      <div class="flex flex-col h-full">
        <div class="flex items-center gap-3 mb-3 flex-shrink-0 flex-wrap">
          <span class="text-base font-semibold text-text">用户管理</span>
          <crm-select
            .options=${[
              { label: '所有角色', value: '' },
              { label: '管理员', value: 'admin' },
              { label: '普通用户', value: 'user' },
              { label: '只读用户', value: 'readonly' },
            ]}
            .value=${this.filterRole}
            @change=${(e: CustomEvent) => { this.filterRole = e.detail.value; this.currentPage = 1; this.loadUsers(); }}
          ></crm-select>
          <crm-select
            .options=${[
              { label: '所有状态', value: '' },
              { label: '启用', value: '1' },
              { label: '禁用', value: '0' },
            ]}
            .value=${this.filterStatus !== null ? String(this.filterStatus) : ''}
            @change=${(e: CustomEvent) => { this.filterStatus = e.detail.value === '' ? null : parseInt(e.detail.value); this.currentPage = 1; this.loadUsers(); }}
          ></crm-select>
          <div class="flex-1"></div>
          <button class="bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer shadow-sm shadow-primary/20 flex items-center gap-2" @click=${() => this.openModal()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>新增用户
          </button>
          <crm-table-toolbar
            .total=${this.total}
            .label=${'个用户'}
            .loading=${this.isLoading}
            @crm-toolbar-refresh=${() => this.loadUsers()}
          ></crm-table-toolbar>
        </div>

        <!-- Table area -->
        <div class="flex-1 min-h-0">
          <div class="bg-surface rounded-md shadow-lg flex flex-col h-full overflow-hidden relative">
            <crm-loading-overlay .active=${this.isLoading}></crm-loading-overlay>
            <div class="overflow-auto flex-1 min-h-0">
              <table class="w-full">
                <thead>
                  <tr class="border-b border-border">
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">用户名</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">邮箱</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">角色</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">状态</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">QQ</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Skype</th>
                    <th class="px-4 py-2.5 text-center text-xs font-medium text-text-secondary uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  ${this.users.length === 0 && !this.isLoading
                    ? html`<tr><td colspan="7" class="text-center py-12 text-text-muted">暂无用户数据</td></tr>`
                    : this.users.map(user => html`
                      <tr class="hover:bg-bg/50 transition-colors border-b border-border">
                        <td class="px-4 py-2 text-sm font-medium text-text">${user.username}</td>
                        <td class="px-4 py-2 text-sm text-text">${user.email_address || '-'}</td>
                        <td class="px-4 py-2"><span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${this.getRoleBadgeClass(user.role)}">${this.getRoleLabel(user.role)}</span></td>
                        <td class="px-4 py-2">
                          ${user.status === 1
                            ? html`<span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-success/10 text-success">启用</span>`
                            : html`<span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-error/10 text-error">禁用</span>`}
                        </td>
                        <td class="px-4 py-2 text-sm text-text">${user.qq || '-'}</td>
                        <td class="px-4 py-2 text-sm text-text">${user.skype || '-'}</td>
                        <td class="px-4 py-2 flex gap-1.5 flex-wrap">
                          <button class="text-primary hover:text-primary-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.openModal(user)}>编辑</button>
                          <button class="text-error hover:text-error-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.handleDelete(user)}>删除</button>
                        </td>
                      </tr>
                    `)}
                </tbody>
              </table>
            </div>
            <crm-pagination
              .current=${this.currentPage}
              .perPage=${this.perPage}
              .total=${this.total}
              @page-change=${(e: CustomEvent) => this.handlePageChange(e.detail.page)}
            ></crm-pagination>
          </div>
        </div>

        ${this.showModal ? this.renderModal(this.editingUser ? '编辑用户' : '新增用户', html`
          <form @submit=${this.handleSubmit} class="space-y-4">
            <div>
              <label class="block mb-1.5 text-sm font-medium text-text">用户名 *</label>
              <input type="text" name="username" required class="w-full select px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingUser?.username || ''} />
            </div>
            <div>
              <label class="block mb-1.5 text-sm font-medium text-text">密码 ${this.editingUser ? '(留空不修改)' : '*'}</label>
              <input type="password" name="password" ?required=${!this.editingUser} class="w-full select px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" />
            </div>
            <div>
              <label class="block mb-1.5 text-sm font-medium text-text">邮箱</label>
              <input type="email" name="email_address" class="w-full select px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingUser?.email_address || ''} />
            </div>
            <div>
              <label class="block mb-1.5 text-sm font-medium text-text">角色</label>
              <crm-select
                fullWidth
                .options=${[
                  { label: '普通用户', value: 'user' },
                  { label: '管理员', value: 'admin' },
                  { label: '只读用户', value: 'readonly' },
                ]}
                .value=${this.formRole}
                @change=${(e: CustomEvent) => { this.formRole = e.detail.value; }}
              ></crm-select>
            </div>
            <div>
              <label class="block mb-1.5 text-sm font-medium text-text">状态</label>
              <crm-select
                fullWidth
                .options=${[
                  { label: '启用', value: '1' },
                  { label: '禁用', value: '0' },
                ]}
                .value=${this.formStatus}
                @change=${(e: CustomEvent) => { this.formStatus = e.detail.value; }}
              ></crm-select>
            </div>
            <div>
              <label class="block mb-1.5 text-sm font-medium text-text">QQ</label>
              <input type="text" name="qq" class="w-full select px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingUser?.qq || ''} />
            </div>
            <div>
              <label class="block mb-1.5 text-sm font-medium text-text">Skype</label>
              <input type="text" name="skype" class="w-full select px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingUser?.skype || ''} />
            </div>
            <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-border">
              <button type="button" class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${this.closeModal}>取消</button>
              <button type="submit" class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20">保存</button>
            </div>
          </form>
        `, this.closeModal) : ''}
      </div>
    `;
  }
}
